import { useEffect, useState } from "react";

import { Auth } from "aws-amplify";
import { Controller, useForm } from "react-hook-form";

import {
  Button,
  Icon,
  Input,
  Text,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import { UserActions } from "~/v2/api/auth";
import { useGetTradingAccount, useMutationRequest } from "~/v2/api/hooks";
import { Field } from "~/v2/components/form";
import { MFASettings } from "~/v2/components/MFASettings/MFASettings";
import { useMFA } from "~/v2/hooks/useMFA";

type Woof = {
  old_password: string;
  new_password: string;
};

export default () => {
  const { account } = useGetTradingAccount();
  const [isRemembered, setIsRemembered] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<Woof>({
    mode: "all",
  });

  const mutation = useMutationRequest(({ old_password, new_password }: Woof) =>
    UserActions.getCurrentUser().then((user) => {
      return Auth.changePassword(user, old_password, new_password);
    }),
  );

  const mfa = useMFA();
  const isMFAEnabled = mfa.isMFAEnabled;

  const onSubmit = (data: Woof) => {
    reset({ old_password: "", new_password: "" });

    mutation.mutate(data, {
      onSuccess: () => {
        toastSuccess("Password updated");
      },
      onError: (error) => {
        console.error(error);
        toastError("Password update failed");
      },
    });
  };

  const onToggleDeviceTrackingClicked = async () => {
    if (isRemembered) {
      await UserActions.forgetDevice();
      await UserActions.userLogout({ global: true });
    } else {
      await UserActions.rememberDevice();
      window.location.reload();
    }
  };

  const onForgetAllDevicesClicked = async () => {
    await UserActions.forgetAllDevices();
    await UserActions.userLogout({ global: true });
  };

  const checkRemembered = async () => {
    const isRemembered = await UserActions.isDeviceRemembered();
    setIsRemembered(isRemembered);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: account?.id is the trigger: the remembered-device check re-runs when the account changes. checkRemembered is redefined every render and writes state, so listing it would loop.
  useEffect(() => {
    checkRemembered();
  }, [account?.id]);

  return (
    <div className="w-full max-w-[600px]">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex w-full max-w-[600px] flex-col">
          <h2 className="mb-5 font-bold text-xl">
            Update Two-Factor Authentication
          </h2>
          <MFASettings
            mfaMethod={mfa.method}
            TOTPQRCode={mfa.TOTPQRCode}
            TOTPSetupVerificationError={mfa.TOTPSetupVerificationError}
            onRefreshMFA={mfa.refresh}
            onDisableMFA={mfa.disable}
            onSetupTOTP={mfa.setupTOTP}
            onVerifyTOTPToken={mfa.verifyTOTPToken}
          />
          {isMFAEnabled && (
            <div>
              <h3 className="mt-8 mb-5 font-bold text-base">
                Update Device Tracking
              </h3>
              <Text className="text-sm">
                We use device tracking to allow you to log in on the same device
                without having to input a two-factor authentication code every
                time.
              </Text>
              <Text className="font-bold text-sm">
                Do not remember shared devices.
              </Text>
              <Text className="mt-2 text-sm">
                {isRemembered
                  ? "Device tracking is enabled for this device."
                  : "Device tracking is not enabled for this device."}
              </Text>
              <div className="mt-4 flex justify-between">
                <Button
                  variant="alternate"
                  onClick={onToggleDeviceTrackingClicked}
                  type="button"
                >
                  {isRemembered ? "Forget this device" : "Remember this device"}
                </Button>
                {isRemembered && (
                  <Button
                    variant="red"
                    onClick={onForgetAllDevicesClicked}
                    type="button"
                  >
                    Forget all devices
                  </Button>
                )}
              </div>
              {isRemembered && (
                <Text className="mt-2 text-sm italic">
                  Note: Forgetting device(s) will log you out.
                </Text>
              )}
            </div>
          )}
        </div>
        <div className="mt-8 flex flex-col">
          <h3 className="mt-8 mb-5 font-bold text-base">Update Password</h3>
          {!isMFAEnabled && (
            <div className="mb-4 flex items-center bg-red-400 p-2 text-sm">
              <Icon name="ExclamationCircle" size="xl" className="mr-2" />
              To update your password you must enable multi-factor
              authentication.
            </div>
          )}
          <div>
            <Field label="Current Password">
              <Controller
                name="old_password"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="password"
                    placeholder="••••••••••••"
                    disabled={!isMFAEnabled}
                  />
                )}
              />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="New Password">
              <Controller
                name="new_password"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="password"
                    placeholder="••••••••••••"
                    disabled={!isMFAEnabled}
                  />
                )}
              />
            </Field>
          </div>
          <Button
            className="mt-8 mr-auto"
            disabled={!isDirty || !isMFAEnabled}
            type="submit"
          >
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
};
